import React, { useState, useEffect } from 'react';
import {
    Save,
    AlertCircle,
    CheckCircle,
    Mail,
    Clock,
    AlertTriangle,
    Shield,
    Zap,
    Plus,
    X,
    Loader2,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { apiClient, NotificationSettings as NotificationSettingsType } from '../services/api';

interface NotificationSettingsProps {
    user: any;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ user }) => {
    const [settings, setSettings] = useState<NotificationSettingsType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Email input states for each section
    const [licenceEmailInput, setLicenceEmailInput] = useState('');
    const [consentEmailInput, setConsentEmailInput] = useState('');
    const [complianceEmailInput, setComplianceEmailInput] = useState('');
    const [bulkEmailInput, setBulkEmailInput] = useState('');
    const [digestEmailInput, setDigestEmailInput] = useState('');
    const [consentActionDigestEmailInput, setConsentActionDigestEmailInput] = useState('');

    // Manual check state
    const [runningManualCheck, setRunningManualCheck] = useState<{
        licence: boolean;
        consent: boolean;
        compliance: boolean;
        digest: boolean;
    }>({
        licence: false,
        consent: false,
        compliance: false,
        digest: false
    });
    const [manualCheckResults, setManualCheckResults] = useState<{
        licence?: { sent: number; failed: number };
        consent?: { sent: number; failed: number };
        compliance?: { sent: number; failed: number };
        digest?: { recipients: string[]; sentAt: string };
    }>({});

    // Collapse state - all collapsed by default
    const [collapsedSections, setCollapsedSections] = useState<{
        licence: boolean;
        consent: boolean;
        compliance: boolean;
        bulk: boolean;
        digest: boolean;
        consentActionDigest: boolean;
    }>({
        licence: true,
        consent: true,
        compliance: true,
        bulk: true,
        digest: true,
        consentActionDigest: true
    });

    // Store initial settings for change detection
    const [initialSettings, setInitialSettings] = useState<NotificationSettingsType | null>(null);

    const toggleSection = (section: keyof typeof collapsedSections) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Check if settings have changed
    const hasChanges = () => {
        if (!settings || !initialSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(initialSettings);
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getNotificationSettings();
            setSettings(response.data);
            setInitialSettings(JSON.parse(JSON.stringify(response.data))); // Deep copy for comparison
        } catch (err: any) {
            setError(err.message || 'Failed to load notification settings');
            console.error('Error loading settings:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!settings) return;

        try {
            setIsSaving(true);
            setError(null);
            setSuccessMessage(null);

            await apiClient.updateNotificationSettings(settings);
            setInitialSettings(JSON.parse(JSON.stringify(settings))); // Update initial settings after successful save
            setSuccessMessage('Notification settings saved successfully');

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to save notification settings');
        } finally {
            setIsSaving(false);
        }
    };

    const addEmail = async (type: 'licence' | 'consent' | 'compliance' | 'bulk' | 'digest' | 'consentActionDigest', email: string) => {
        if (!settings) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        const fieldMap = {
            licence: 'licenceExpiryRecipients',
            consent: 'consentExpiryRecipients',
            compliance: 'complianceRecipients',
            bulk: 'bulkRecipients',
            digest: 'dailyDigestRecipients',
            consentActionDigest: 'consentActionDigestRecipients'
        } as const;

        const field = fieldMap[type];
        const recipients = settings[field] as string[];

        if (recipients.includes(email)) {
            setError('This email is already in the list');
            return;
        }

        // Update local state
        const updatedSettings = {
            ...settings,
            [field]: [...recipients, email]
        };
        
        setSettings(updatedSettings);

        // Clear input
        switch (type) {
            case 'licence': setLicenceEmailInput(''); break;
            case 'consent': setConsentEmailInput(''); break;
            case 'compliance': setComplianceEmailInput(''); break;
            case 'bulk': setBulkEmailInput(''); break;
            case 'digest': setDigestEmailInput(''); break;
            case 'consentActionDigest': setConsentActionDigestEmailInput(''); break;
        }

        setError(null);

        // AUTO-SAVE: Immediately persist to database
        try {
            setIsSaving(true);
            await apiClient.updateNotificationSettings(updatedSettings);
            // Silent save - no success message to avoid disrupting user flow
        } catch (err: any) {
            setError(`Failed to save email: ${err.message || 'Unknown error'}`);
            // Revert the local state on error
            setSettings(settings);
        } finally {
            setIsSaving(false);
        }
    };

    const removeEmail = async (type: 'licence' | 'consent' | 'compliance' | 'bulk' | 'digest' | 'consentActionDigest', email: string) => {
        if (!settings) return;

        const fieldMap = {
            licence: 'licenceExpiryRecipients',
            consent: 'consentExpiryRecipients',
            compliance: 'complianceRecipients',
            bulk: 'bulkRecipients',
            digest: 'dailyDigestRecipients',
            consentActionDigest: 'consentActionDigestRecipients'
        } as const;

        const field = fieldMap[type];
        const recipients = settings[field] as string[];

        // Update local state
        const updatedSettings = {
            ...settings,
            [field]: recipients.filter(e => e !== email)
        };
        
        setSettings(updatedSettings);

        // AUTO-SAVE: Immediately persist to database
        try {
            setIsSaving(true);
            await apiClient.updateNotificationSettings(updatedSettings);
            // Silent save - no success message to avoid disrupting user flow
        } catch (err: any) {
            setError(`Failed to remove email: ${err.message || 'Unknown error'}`);
            // Revert the local state on error
            setSettings(settings);
        } finally {
            setIsSaving(false);
        }
    };

    const runManualLicenceCheck = async () => {
        try {
            setRunningManualCheck({ ...runningManualCheck, licence: true });
            setError(null);
            setManualCheckResults({ ...manualCheckResults, licence: undefined });

            const response = await apiClient.triggerLicenceExpiryNotifications();
            
            setManualCheckResults({ 
                ...manualCheckResults, 
                licence: response.data 
            });
            
            const { sent, failed } = response.data;
            if (sent > 0 || failed > 0) {
                setSuccessMessage(
                    `Manual licence check complete: ${sent} notification${sent === 1 ? '' : 's'} sent${failed > 0 ? `, ${failed} failed` : ''}`
                );
            } else {
                setSuccessMessage('Manual licence check complete: No expiring licences found');
            }

            // Clear message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
                setManualCheckResults({ ...manualCheckResults, licence: undefined });
            }, 5000);
        } catch (err: any) {
            setError(`Failed to run manual licence check: ${err.message}`);
        } finally {
            setRunningManualCheck({ ...runningManualCheck, licence: false });
        }
    };

    const runManualConsentCheck = async () => {
        try {
            setRunningManualCheck({ ...runningManualCheck, consent: true });
            setError(null);
            setManualCheckResults({ ...manualCheckResults, consent: undefined });

            const response = await apiClient.triggerConsentExpiryNotifications();
            
            setManualCheckResults({ 
                ...manualCheckResults, 
                consent: response.data 
            });
            
            const { sent, failed } = response.data;
            if (sent > 0 || failed > 0) {
                setSuccessMessage(
                    `Manual consent check complete: ${sent} notification${sent === 1 ? '' : 's'} sent${failed > 0 ? `, ${failed} failed` : ''}`
                );
            } else {
                setSuccessMessage('Manual consent check complete: No expiring consents found');
            }

            // Clear message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
                setManualCheckResults({ ...manualCheckResults, consent: undefined });
            }, 5000);
        } catch (err: any) {
            setError(`Failed to run manual consent check: ${err.message}`);
        } finally {
            setRunningManualCheck({ ...runningManualCheck, consent: false });
        }
    };

    const runManualComplianceCheck = async () => {
        try {
            setRunningManualCheck({ ...runningManualCheck, compliance: true });
            setError(null);
            setManualCheckResults({ ...manualCheckResults, compliance: undefined });

            const response = await apiClient.triggerComplianceCheck();

            setManualCheckResults({
                ...manualCheckResults,
                compliance: response.data
            });

            const { sent, failed } = response.data;
            if (sent > 0 || failed > 0) {
                setSuccessMessage(
                    `Manual compliance check complete: ${sent} alert${sent === 1 ? '' : 's'} sent${failed > 0 ? `, ${failed} failed` : ''}`
                );
            } else {
                setSuccessMessage('Manual compliance check complete: No compliance issues found');
            }

            // Clear message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
                setManualCheckResults({ ...manualCheckResults, compliance: undefined });
            }, 5000);
        } catch (err: any) {
            setError(`Failed to run manual compliance check: ${err.message}`);
        } finally {
            setRunningManualCheck({ ...runningManualCheck, compliance: false });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
                <span className="ml-3 text-gray-600">Loading settings...</span>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                    <div>
                        <h3 className="text-sm font-medium text-red-800">Failed to Load Settings</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
                <button
                    onClick={loadSettings}
                    className="mt-4 px-4 py-2 text-sm text-white rounded-md hover:opacity-90"
                    style={{ backgroundColor: '#69BE28' }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Success Message */}
            {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        <p className="text-sm text-green-800">{successMessage}</p>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                </div>
            )}

            {/* Licence Expiry Alerts */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleSection('licence')}
                    >
                        <AlertTriangle className="w-6 h-6 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Licence Expiry Alerts</h3>
                            <p className="text-sm text-gray-500">Automated alerts for expiring driver licences</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={settings.licenceExpiryEnabled}
                                onChange={(e) => setSettings({ ...settings, licenceExpiryEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <button
                            onClick={() => toggleSection('licence')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            {collapsedSections.licence ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronUp className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>

                {!collapsedSections.licence && (
                    <div className={`space-y-4 mt-4 pt-4 border-t ${!settings.licenceExpiryEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">How This Works:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>System checks ALL drivers in your organisation daily at 7:00 AM UTC</li>
                                        <li>Emails are sent to the recipients below (NOT to drivers themselves)</li>
                                        <li>One email per threshold lists all drivers expiring at that interval</li>
                                        <li>Example: "3 drivers have licences expiring in 30 days"</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Alert Days */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Send alerts when licence expires in (days):
                            </label>
                            <div className="flex gap-2">
                                {[30, 14, 7, 1].map((days) => (
                                    <label key={days} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={settings.licenceExpiryDays.includes(days)}
                                            disabled={!settings.licenceExpiryEnabled}
                                            onChange={(e) => {
                                                const newDays = e.target.checked
                                                    ? [...settings.licenceExpiryDays, days].sort((a, b) => b - a)
                                                    : settings.licenceExpiryDays.filter(d => d !== days);
                                                setSettings({ ...settings, licenceExpiryDays: newDays });
                                            }}
                                            className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{days} days</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Recipients */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Recipients:
                            </label>
                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <input
                                        type="email"
                                        value={licenceEmailInput}
                                        disabled={!settings.licenceExpiryEnabled}
                                        onChange={(e) => setLicenceEmailInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addEmail('licence', licenceEmailInput);
                                            }
                                        }}
                                        placeholder="Enter email address (e.g., compliance@company.com)"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                    />
                                    <button
                                        onClick={() => addEmail('licence', licenceEmailInput)}
                                        disabled={!licenceEmailInput || !settings.licenceExpiryEnabled}
                                        className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: '#69BE28' }}
                                        title="Add email recipient"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                {licenceEmailInput && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(licenceEmailInput) && (
                                    <p className="text-xs text-red-600">
                                        Please enter a valid email address
                                    </p>
                                )}
                                <p className="text-xs text-gray-500">
                                    These recipients will be notified when any driver's licence is expiring
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.licenceExpiryRecipients.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {email}
                                        <button
                                            onClick={() => removeEmail('licence', email)}
                                            disabled={!settings.licenceExpiryEnabled}
                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Remove email"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Manual Check Button */}
                        {settings.licenceExpiryRecipients.length > 0 && (
                            <div className="pt-4 border-t">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                                    <div className="flex items-start">
                                        <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-yellow-800">
                                            <p className="font-medium">Manual Check</p>
                                            <p className="text-xs mt-1">
                                                This will immediately check all drivers and send real alerts to the configured recipients above.
                                                Use this to test after making configuration changes.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={runManualLicenceCheck}
                                    disabled={runningManualCheck.licence || !settings.licenceExpiryEnabled}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {runningManualCheck.licence ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Checking Drivers...
                                        </>
                                    ) : (
                                        <>
                                            <AlertTriangle className="w-4 h-4 mr-2" />
                                            Run Manual Licence Check Now
                                        </>
                                    )}
                                </button>
                                {manualCheckResults.licence && (
                                    <div className="mt-2 text-sm text-gray-600">
                                        <CheckCircle className="w-4 h-4 inline text-green-600 mr-1" />
                                        Found {manualCheckResults.licence.sent > 0 ? 'expiring licences' : 'no expiring licences'}.
                                        {manualCheckResults.licence.sent > 0 && ` Sent ${manualCheckResults.licence.sent} notification${manualCheckResults.licence.sent === 1 ? '' : 's'}.`}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Consent Expiry Alerts */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleSection('consent')}
                    >
                        <Shield className="w-6 h-6 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Consent Expiry Alerts</h3>
                            <p className="text-sm text-gray-500">Automated alerts for expiring D906 consents</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={settings.consentExpiryEnabled}
                                onChange={(e) => setSettings({ ...settings, consentExpiryEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <button
                            onClick={() => toggleSection('consent')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            {collapsedSections.consent ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronUp className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>

                {!collapsedSections.consent && (
                    <div className={`space-y-4 mt-4 pt-4 border-t ${!settings.consentExpiryEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">How This Works:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>System checks D906 consent expiry dates daily at 7:00 AM UTC</li>
                                        <li>Alerts sent to recipients below when consent is expiring soon</li>
                                        <li>Includes drivers who need consent renewal or initial consent</li>
                                        <li>Take action by sending new D906 consent requests</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Alert Days */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Send alerts when consent expires in (days):
                            </label>
                            <div className="flex gap-2">
                                {[30, 14, 7].map((days) => (
                                    <label key={days} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={settings.consentExpiryDays.includes(days)}
                                            disabled={!settings.consentExpiryEnabled}
                                            onChange={(e) => {
                                                const newDays = e.target.checked
                                                    ? [...settings.consentExpiryDays, days].sort((a, b) => b - a)
                                                    : settings.consentExpiryDays.filter(d => d !== days);
                                                setSettings({ ...settings, consentExpiryDays: newDays });
                                            }}
                                            className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{days} days</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Recipients */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Recipients:
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="email"
                                    value={consentEmailInput}
                                    disabled={!settings.consentExpiryEnabled}
                                    onChange={(e) => setConsentEmailInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addEmail('consent', consentEmailInput);
                                        }
                                    }}
                                    placeholder="Enter email address"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                                <button
                                    onClick={() => addEmail('consent', consentEmailInput)}
                                    disabled={!consentEmailInput || !settings.consentExpiryEnabled}
                                    className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.consentExpiryRecipients.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {email}
                                        <button
                                            onClick={() => removeEmail('consent', email)}
                                            disabled={!settings.consentExpiryEnabled}
                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Manual Check Button */}
                        {settings.consentExpiryRecipients.length > 0 && (
                            <div className="pt-4 border-t">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                                    <div className="flex items-start">
                                        <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-yellow-800">
                                            <p className="font-medium">Manual Check</p>
                                            <p className="text-xs mt-1">
                                                This will immediately check all driver consents and send real alerts to the configured recipients above. 
                                                Use this to test after making configuration changes.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={runManualConsentCheck}
                                    disabled={runningManualCheck.consent || !settings.consentExpiryEnabled}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {runningManualCheck.consent ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Checking Consents...
                                        </>
                                    ) : (
                                        <>
                                            <Shield className="w-4 h-4 mr-2" />
                                            Run Manual Consent Check Now
                                        </>
                                    )}
                                </button>
                                {manualCheckResults.consent && (
                                    <div className="mt-2 text-sm text-gray-600">
                                        <CheckCircle className="w-4 h-4 inline text-green-600 mr-1" />
                                        Found {manualCheckResults.consent.sent > 0 ? 'expiring consents' : 'no expiring consents'}.
                                        {manualCheckResults.consent.sent > 0 && ` Sent ${manualCheckResults.consent.sent} notification${manualCheckResults.consent.sent === 1 ? '' : 's'}.`}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Compliance Alerts */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleSection('compliance')}
                    >
                        <AlertCircle className="w-6 h-6 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Compliance Issue Alerts</h3>
                            <p className="text-sm text-gray-500">Daily scheduled alerts for drivers with compliance issues</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={settings.complianceAlertsEnabled}
                                onChange={(e) => setSettings({ ...settings, complianceAlertsEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <button
                            onClick={() => toggleSection('compliance')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            {collapsedSections.compliance ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronUp className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>

                {!collapsedSections.compliance && (
                    <div className={`space-y-4 mt-4 pt-4 border-t ${!settings.complianceAlertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">How This Works:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>Scheduled daily check of all drivers for compliance issues</li>
                                        <li>One consolidated email per day at your chosen time</li>
                                        <li>Filter by severity: all issues, medium/high, or high only</li>
                                        <li>Examples: endorsements, restrictions, suspended licences</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Severity Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Alert Severity:
                            </label>
                            <select
                                value={settings.complianceSeverity}
                                disabled={!settings.complianceAlertsEnabled}
                                onChange={(e) => setSettings({ ...settings, complianceSeverity: e.target.value as any })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                            >
                                <option value="all">All Issues</option>
                                <option value="medium_high">Medium & High Priority</option>
                                <option value="high_only">High Priority Only</option>
                            </select>
                        </div>

                        {/* Check Time */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Daily Check Time (UTC):
                            </label>
                            <input
                                type="time"
                                value={settings.complianceCheckTime}
                                disabled={!settings.complianceAlertsEnabled}
                                onChange={(e) => setSettings({ ...settings, complianceCheckTime: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Default: 07:30 (7:30 AM). Compliance checks will run once daily at this time.
                            </p>
                        </div>

                        {/* Recipients */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Recipients:
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="email"
                                    value={complianceEmailInput}
                                    disabled={!settings.complianceAlertsEnabled}
                                    onChange={(e) => setComplianceEmailInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addEmail('compliance', complianceEmailInput);
                                        }
                                    }}
                                    placeholder="Enter email address"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                                <button
                                    onClick={() => addEmail('compliance', complianceEmailInput)}
                                    disabled={!complianceEmailInput || !settings.complianceAlertsEnabled}
                                    className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.complianceRecipients.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {email}
                                        <button
                                            onClick={() => removeEmail('compliance', email)}
                                            disabled={!settings.complianceAlertsEnabled}
                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Manual Check Button */}
                        {settings.complianceRecipients.length > 0 && (
                            <div className="pt-4 border-t">
                                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                                    <div className="flex items-start">
                                        <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-yellow-800">
                                            <p className="font-medium">Manual Check</p>
                                            <p className="text-xs mt-1">
                                                This will immediately check all drivers for compliance issues and send real alerts to the configured recipients above.
                                                Use this to test after making configuration changes.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={runManualComplianceCheck}
                                    disabled={runningManualCheck.compliance || !settings.complianceAlertsEnabled}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {runningManualCheck.compliance ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Checking Compliance...
                                        </>
                                    ) : (
                                        <>
                                            <AlertCircle className="w-4 h-4 mr-2" />
                                            Run Manual Compliance Check Now
                                        </>
                                    )}
                                </button>
                                {manualCheckResults.compliance && (
                                    <div className="mt-2 text-sm text-gray-600">
                                        <CheckCircle className="w-4 h-4 inline text-green-600 mr-1" />
                                        Found {manualCheckResults.compliance.sent > 0 ? 'compliance issues' : 'no compliance issues'}.
                                        {manualCheckResults.compliance.sent > 0 && ` Sent ${manualCheckResults.compliance.sent} alert${manualCheckResults.compliance.sent === 1 ? '' : 's'}.`}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Bulk Operation Alerts */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleSection('bulk')}
                    >
                        <Zap className="w-6 h-6 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Bulk Operation Alerts</h3>
                            <p className="text-sm text-gray-500">Status updates for bulk driver imports and licence checks</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={settings.bulkAlertsEnabled}
                                onChange={(e) => setSettings({ ...settings, bulkAlertsEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <button
                            onClick={() => toggleSection('bulk')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            {collapsedSections.bulk ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronUp className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>

                {!collapsedSections.bulk && (
                    <div className={`space-y-4 mt-4 pt-4 border-t ${!settings.bulkAlertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">How This Works:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>Status updates when bulk driver imports complete</li>
                                        <li>Notifications when bulk licence checks finish processing</li>
                                        <li>Includes success/failure counts and error details</li>
                                        <li>Sent to recipients when operations complete (may take minutes to hours)</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Recipients */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Recipients:
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="email"
                                    value={bulkEmailInput}
                                    disabled={!settings.bulkAlertsEnabled}
                                    onChange={(e) => setBulkEmailInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addEmail('bulk', bulkEmailInput);
                                        }
                                    }}
                                    placeholder="Enter email address"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                                <button
                                    onClick={() => addEmail('bulk', bulkEmailInput)}
                                    disabled={!bulkEmailInput || !settings.bulkAlertsEnabled}
                                    className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.bulkRecipients.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {email}
                                        <button
                                            onClick={() => removeEmail('bulk', email)}
                                            disabled={!settings.bulkAlertsEnabled}
                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Daily Digest */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleSection('digest')}
                    >
                        <Clock className="w-6 h-6 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Daily Digest Email</h3>
                            <p className="text-sm text-gray-500">Scheduled summary of all compliance activity</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={settings.dailyDigestEnabled}
                                onChange={(e) => setSettings({ ...settings, dailyDigestEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <button
                            onClick={() => toggleSection('digest')}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            {collapsedSections.digest ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronUp className="w-5 h-5" />
                            )}
                        </button>
                    </div>
                </div>

                {!collapsedSections.digest && (
                    <div className={`space-y-4 mt-4 pt-4 border-t ${!settings.dailyDigestEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        {/* Info Box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">How This Works:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>One consolidated email sent daily at your chosen time</li>
                                        <li>Includes summary of all selected compliance activities</li>
                                        <li>Reduces email volume while keeping you informed</li>
                                        <li>Choose what to include: expiry warnings, compliance issues, etc.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Send Time */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Send Time (UTC):
                            </label>
                            <input
                                type="time"
                                value={settings.dailyDigestTime}
                                disabled={!settings.dailyDigestEnabled}
                                onChange={(e) => setSettings({ ...settings, dailyDigestTime: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Time of day to send the digest.
                            </p>
                        </div>

                        {/* Frequency */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Frequency:
                            </label>
                            <select
                                value={settings.dailyDigestFrequency}
                                disabled={!settings.dailyDigestEnabled}
                                onChange={(e) => setSettings({ ...settings, dailyDigestFrequency: e.target.value as any })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                            >
                                <option value="daily">Daily</option>
                                <option value="7days">Every 7 Days</option>
                                <option value="14days">Every 14 Days</option>
                                <option value="30days">Every 30 Days</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                How often to send the digest email.
                            </p>
                        </div>

                        {/* Include in Digest */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Include in Digest:
                            </label>
                            <div className="space-y-2">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={settings.dailyDigestIncludeLicenceExpiry}
                                        disabled={!settings.dailyDigestEnabled}
                                        onChange={(e) => setSettings({ ...settings, dailyDigestIncludeLicenceExpiry: e.target.checked })}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Licence Expiry Warnings</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={settings.dailyDigestIncludeConsentExpiry}
                                        disabled={!settings.dailyDigestEnabled}
                                        onChange={(e) => setSettings({ ...settings, dailyDigestIncludeConsentExpiry: e.target.checked })}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Consent Expiry Warnings</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={settings.dailyDigestIncludeCompliance}
                                        disabled={!settings.dailyDigestEnabled}
                                        onChange={(e) => setSettings({ ...settings, dailyDigestIncludeCompliance: e.target.checked })}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Compliance Issues</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={settings.dailyDigestIncludeBulkOperations || false}
                                        disabled={!settings.dailyDigestEnabled}
                                        onChange={(e) => setSettings({ ...settings, dailyDigestIncludeBulkOperations: e.target.checked })}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Bulk Operation Results</span>
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                                Select which sections to include in your daily digest email
                            </p>
                        </div>

                        {/* Recipients */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Recipients:
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="email"
                                    value={digestEmailInput}
                                    disabled={!settings.dailyDigestEnabled}
                                    onChange={(e) => setDigestEmailInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addEmail('digest', digestEmailInput);
                                        }
                                    }}
                                    placeholder="Enter email address"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                                <button
                                    onClick={() => addEmail('digest', digestEmailInput)}
                                    disabled={!digestEmailInput || !settings.dailyDigestEnabled}
                                    className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.dailyDigestRecipients.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {email}
                                        <button
                                            onClick={() => removeEmail('digest', email)}
                                            disabled={!settings.dailyDigestEnabled}
                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Consent Action Digest */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleSection('consentActionDigest')}
                    >
                        <Mail className="w-6 h-6 mr-3" style={{ color: '#69BE28' }} />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Consent Action Digest</h3>
                            <p className="text-sm text-gray-500">Daily summary of driver consent approvals and declines</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={settings.consentActionDigestEnabled}
                                onChange={(e) => setSettings({ ...settings, consentActionDigestEnabled: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <button
                            onClick={() => toggleSection('consentActionDigest')}
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            {collapsedSections.consentActionDigest ? (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                                <ChevronUp className="w-5 h-5 text-gray-500" />
                            )}
                        </button>
                    </div>
                </div>

                {!collapsedSections.consentActionDigest && (
                    <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <AlertCircle className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium mb-1">Daily Consent Activity Summary</p>
                                    <p>Receive one consolidated daily email with all driver consent actions (approvals, declines, and withdrawals) from the previous day. The digest is sent at your configured time each morning.</p>
                                    <p className="mt-2"><strong>Note:</strong> Drivers still receive immediate confirmation emails when they approve or decline consent.</p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Digest Time (UTC)
                            </label>
                            <input
                                type="time"
                                value={settings.consentActionDigestTime}
                                onChange={(e) => setSettings({ ...settings, consentActionDigestTime: e.target.value })}
                                disabled={!settings.consentActionDigestEnabled}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Current setting: {settings.consentActionDigestTime} UTC (Default: 09:00 AM UTC)
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Recipients
                            </label>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="email"
                                    value={consentActionDigestEmailInput}
                                    onChange={(e) => setConsentActionDigestEmailInput(e.target.value)}
                                    disabled={!settings.consentActionDigestEnabled}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addEmail('consentActionDigest', consentActionDigestEmailInput);
                                        }
                                    }}
                                    placeholder="Enter email address"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                                <button
                                    onClick={() => addEmail('consentActionDigest', consentActionDigestEmailInput)}
                                    disabled={!consentActionDigestEmailInput || !settings.consentActionDigestEnabled}
                                    className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {settings.consentActionDigestRecipients.map((email) => (
                                    <span
                                        key={email}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        {email}
                                        <button
                                            onClick={() => removeEmail('consentActionDigest', email)}
                                            disabled={!settings.consentActionDigestEnabled}
                                            className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges()}
                    className="inline-flex items-center px-6 py-3 text-white font-medium rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                    style={{ backgroundColor: '#69BE28' }}
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="w-5 h-5 mr-2" />
                            Save Settings
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default NotificationSettings;